import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use';

import { Plus, Minus } from '@common/components/Icons';

import {
  QuantityPickerInput,
  QuantityPickerButton,
  QuantityPickerWrapper,
  ErrorMessage,
} from './QuantityPicker.styled';

const handleValidateInput = e =>
  !/^[0-9]{1,}$/g.test(e.key) && e.preventDefault();
const validateQuanityBeforeUpdate = (value, max) => {
  if (value > max) return max;

  if (value < 0) return 0;

  return value;
};

const QuantityPicker = ({
  isDisabled,
  setQuantity,
  quantity,
  max,
  hasError,
  errorMessage,
}) => {
  const [internalQuantity, setInternalQuantity] = useState(quantity);
  const updateQuantity = () => {
    if (
      Number.isInteger(internalQuantity) &&
      internalQuantity !== quantity &&
      internalQuantity !== 0
    ) {
      setQuantity(internalQuantity);
    }
  };

  const [, cancelUpdate] = useDebounce(updateQuantity, 400, [internalQuantity]);

  const handleInputFocus = () => {
    setInternalQuantity('');
  };

  const handleInputBlur = e => {
    if (['', '0'].includes(e.target.value)) {
      setInternalQuantity(quantity);
    } else {
      cancelUpdate();
      updateQuantity();
    }
  };

  const handleUpdateValue = ({ target: { value } }) =>
    setInternalQuantity(
      value !== '' && value !== '0'
        ? validateQuanityBeforeUpdate(parseInt(value, 10), max)
        : '',
    );

  const handleIncrease = () => {
    setInternalQuantity(value => validateQuanityBeforeUpdate(value + 1, max));
  };

  const handleDecrease = () => {
    setInternalQuantity(value => validateQuanityBeforeUpdate(value - 1, max));
  };

  return (
    <>
      <QuantityPickerWrapper
        disabled={isDisabled}
        hasError={hasError}
        data-testid="quantityPicker"
      >
        <QuantityPickerButton
          type="button"
          data-testid="quantityPickerMinusButton"
          onClick={handleDecrease}
          disabled={
            isDisabled ||
            (internalQuantity !== '' && internalQuantity <= 1) ||
            (internalQuantity === '' && quantity === 1)
          }
          isLeft
        >
          <Minus />
        </QuantityPickerButton>
        <QuantityPickerInput
          type="number"
          pattern="\d*"
          inputMode="decimal"
          value={internalQuantity}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyPress={handleValidateInput}
          onChange={handleUpdateValue}
          min="1"
          max={max}
          disabled={isDisabled}
          hasError={hasError}
        />
        <QuantityPickerButton
          type="button"
          data-testid="quantityPickerPlusButton"
          onClick={handleIncrease}
          disabled={isDisabled || internalQuantity >= max}
        >
          <Plus />
        </QuantityPickerButton>
      </QuantityPickerWrapper>
      {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </>
  );
};

QuantityPicker.propTypes = {
  isDisabled: PropTypes.bool,
  setQuantity: PropTypes.func.isRequired,
  quantity: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
};

QuantityPicker.defaultProps = {
  isDisabled: false,
  hasError: false,
  errorMessage: '',
};

export default QuantityPicker;
